/**
 * Dual Company Logo Component
 * 
 * Displays two company logos side-by-side for temporary employment situations.
 * - Assignment company logo: left side, normal size
 * - Temporary agency logo: right side, smaller size, aligned to bottom, overlapping
 */

import React from 'react';
import { cn } from '@/lib/utils';

interface DualCompanyLogoProps {
  assignmentCompany: {
    logoPath: string | null;
    logoText: string | null;
    name: string;
  };
  tempAgency: {
    logoPath: string | null;
    logoText: string | null;
    name: string;
  };
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  onAssignmentCompanyClick?: (e: React.MouseEvent) => void;
  onTempAgencyClick?: (e: React.MouseEvent) => void;
}

const sizeClasses = {
  sm: {
    container: 'w-12 h-8',
    assignment: 'w-8 h-6',
    tempAgency: 'w-5 h-5', // Made square for perfect circle, smaller
    tempAgencyPosition: 'right-2', // More overlap for small size (moves left)
  },
  md: {
    container: 'w-16 h-10',
    assignment: 'w-12 h-8',
    tempAgency: 'w-7 h-7', // Made square for perfect circle, smaller
    tempAgencyPosition: 'right-0', // Standard position for medium
  },
  lg: {
    container: 'w-20 h-12',
    assignment: 'w-16 h-10',
    tempAgency: 'w-9 h-9', // Made square for perfect circle, smaller
    tempAgencyPosition: 'right-0', // Standard position for large
  },
};

export default function DualCompanyLogo({ 
  assignmentCompany, 
  tempAgency, 
  className, 
  size = 'md',
  onAssignmentCompanyClick,
  onTempAgencyClick
}: DualCompanyLogoProps) {
  const sizes = sizeClasses[size];

  return (
    <div 
      className={cn('relative flex items-end', sizes.container, className)}
      title={`${assignmentCompany.name} (via ${tempAgency.name})`}
      data-testid="dual-company-logo-container"
    >
      {/* Assignment company logo - left side, normal size */}
      <div 
        className={cn('relative z-10', onAssignmentCompanyClick ? 'cursor-pointer hover:scale-110 transition-transform' : '', sizes.assignment)}
        onClick={onAssignmentCompanyClick ? (e) => { e.stopPropagation(); onAssignmentCompanyClick(e); } : undefined}
        title={assignmentCompany.name}
        data-testid="assignment-company-section"
      >
        {assignmentCompany.logoPath ? (
          <img
            src={assignmentCompany.logoPath}
            alt={`${assignmentCompany.name} logo`}
            className="w-full h-full object-contain"
            onError={(e) => {
              // Fallback to text if image fails to load
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div className={`${assignmentCompany.logoPath ? 'hidden' : ''} absolute inset-0 flex items-center justify-center bg-gray-100 text-xs font-medium text-gray-600 rounded border`}>
          {assignmentCompany.logoText || assignmentCompany.name.charAt(0)}
        </div>
      </div>

      {/* Temporary agency logo - right side, smaller, overlapping */}
      <div className={cn(
        'absolute bottom-0 z-20 bg-background rounded-full shadow-sm', 
        onTempAgencyClick ? 'cursor-pointer hover:scale-110 transition-transform' : '',
        sizes.tempAgency,
        sizes.tempAgencyPosition
      )}
        onClick={onTempAgencyClick ? (e) => { e.stopPropagation(); onTempAgencyClick(e); } : undefined}
        title={tempAgency.name}
        data-testid="temp-agency-section"
      >
        {tempAgency.logoPath ? (
          <img
            src={tempAgency.logoPath}
            alt={`${tempAgency.name} logo`}
            className="w-full h-full object-contain p-1 rounded-full"
            onError={(e) => {
              // Fallback to text if image fails to load
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div className={`${tempAgency.logoPath ? 'hidden' : ''} absolute inset-0 flex items-center justify-center bg-background text-xs font-medium text-gray-600 rounded-full`}>
          {tempAgency.logoText || tempAgency.name.charAt(0)}
        </div>
      </div>
    </div>
  );
}
